import { useState, useEffect } from "react";
import { Package, AlertTriangle, TrendingDown, Search, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInventory() {
  const [data, setData] = useState({ products: [], low_stock: 0, out_of_stock: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editQty, setEditQty] = useState({});

  const fetchData = (f = "") => {
    setLoading(true);
    axios.get(`${API}/admin/inventory?filter=${f}`, { withCredentials: true }).then(r => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(filter); }, [filter]);

  const updateStock = async (pid, qty) => {
    try {
      await axios.put(`${API}/admin/inventory/${pid}`, { stock_qty: parseInt(qty) }, { withCredentials: true });
      toast.success("Stock updated");
      fetchData(filter);
    } catch { toast.error("Failed to update"); }
  };

  const stockColor = (qty) => qty === 0 ? "text-red-600 bg-red-50" : qty < 20 ? "text-amber-600 bg-amber-50" : "text-emerald-600 bg-emerald-50";

  return (
    <div data-testid="admin-inventory-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Inventory Management</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm border"><div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center mb-3"><Package className="w-5 h-5 text-cyan-600" /></div><p className="text-2xl font-bold text-slate-800">{data.products.length}</p><p className="text-xs text-slate-500">Total SKUs</p></div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border"><div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mb-3"><AlertTriangle className="w-5 h-5 text-amber-600" /></div><p className="text-2xl font-bold text-amber-600">{data.low_stock}</p><p className="text-xs text-slate-500">Low Stock Items</p></div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border"><div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center mb-3"><TrendingDown className="w-5 h-5 text-red-600" /></div><p className="text-2xl font-bold text-red-600">{data.out_of_stock}</p><p className="text-xs text-slate-500">Out of Stock</p></div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border"><div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-3"><Package className="w-5 h-5 text-emerald-600" /></div><p className="text-2xl font-bold text-slate-800">Rs.{data.total_value?.toLocaleString()}</p><p className="text-xs text-slate-500">Inventory Value</p></div>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="bg-white rounded-xl border"><TabsTrigger value="" className="rounded-xl text-xs">All Products</TabsTrigger><TabsTrigger value="low" className="rounded-xl text-xs">Low Stock ({data.low_stock})</TabsTrigger></TabsList>
      </Tabs>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b bg-slate-50"><th className="p-4 font-medium">Product</th><th className="p-4 font-medium">Category</th><th className="p-4 font-medium">Price</th><th className="p-4 font-medium">Stock Qty</th><th className="p-4 font-medium">Status</th><th className="p-4 font-medium">Update</th></tr></thead>
          <tbody>
            {loading ? [...Array(5)].map((_, i) => <tr key={i}><td colSpan={6} className="p-4"><div className="h-10 skeleton-loading rounded" /></td></tr>) :
            data.products.map(p => (
              <tr key={p.product_id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="p-4"><div className="flex items-center gap-3"><img src={p.images?.[0] || ""} alt="" className="w-8 h-8 rounded-lg object-cover bg-slate-100" /><div><p className="font-medium text-slate-800 text-xs">{p.name}</p><p className="text-[10px] text-slate-400">{p.brand}</p></div></div></td>
                <td className="p-4 text-slate-600 text-xs">{p.category}</td>
                <td className="p-4 font-medium text-xs">Rs.{p.sale_price}</td>
                <td className="p-4"><Badge className={`text-xs font-bold ${stockColor(p.stock_qty)}`}>{p.stock_qty}</Badge></td>
                <td className="p-4"><Badge className={`text-xs ${p.in_stock ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{p.in_stock ? "In Stock" : "Out"}</Badge></td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Input type="number" defaultValue={p.stock_qty} onChange={e => setEditQty({ ...editQty, [p.product_id]: e.target.value })} className="w-20 h-8 rounded-lg text-xs" />
                    <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => updateStock(p.product_id, editQty[p.product_id] || p.stock_qty)}><RefreshCw className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
